import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { DocumentChartBarIcon } from '@heroicons/react/24/outline';
import ReportTemplate from '../../components/reports/ReportTemplate';
import CSVExportButton from '../../components/reports/CSVExportButton';
import { ROUTES } from '../../consts/general';
import { EADIN_INVENTORY } from '../../consts/reports';
import { formatCurrencyUsdFixedTwo } from '../../helpers/common';

const columns = [
  { key: 'sku', label: 'SKU', type: 'string' },
  { key: 'product', label: 'Product Name', type: 'string' },
  { key: 'physical_on_hand', label: 'Physical On Hand', type: 'number', align: 'right' },
  { key: 'on_order', label: 'On Order', type: 'number', align: 'right' },
  { key: 'kit_buildable', label: 'Kit Buildable', type: 'number', align: 'right' },
  { key: 'unit_weighted_cost', label: 'Unit Weighted Cost', type: 'currency', align: 'right' },
  { key: 'inventory_value', label: 'Inventory Value', type: 'currency', align: 'right' },
  { key: 'data_as_of', label: 'Data As Of', type: 'string' },
];

export const fetchAllEadinInventoryRows = async (get = axios.get) => {
  const endpoint = '/api/v1/reports/eadin-inventory';
  const pageSize = 1000;
  const firstResponse = await get(endpoint, { params: { page: 1, pageSize } });
  const firstPage = firstResponse?.data;
  const rows = Array.isArray(firstPage?.data) ? [...firstPage.data] : [];
  const totalPages = Number(firstPage?.totalPages) || 1;

  for (let page = 2; page <= totalPages; page += 1) {
    const response = await get(endpoint, { params: { page, pageSize } });
    if (Array.isArray(response?.data?.data)) rows.push(...response.data.data);
  }

  return rows;
};

export const getVisibleInventoryRows = (rows, {
  search = '',
  sortField = null,
  sortDirection = 'asc',
} = {}) => {
  const searchTerm = String(search || '').trim().toLowerCase();
  const visibleRows = (Array.isArray(rows) ? rows : []).filter((row) => {
    if (!searchTerm) return true;
    return [row?.sku, row?.product]
      .some((value) => String(value || '').toLowerCase().includes(searchTerm));
  });

  if (!sortField) return visibleRows;

  const sortColumn = columns.find((column) => column.key === sortField);
  const direction = sortDirection === 'desc' ? -1 : 1;

  return [...visibleRows].sort((left, right) => {
    if (sortColumn?.type === 'number' || sortColumn?.type === 'currency') {
      const leftNumber = Number(left?.[sortField] ?? 0);
      const rightNumber = Number(right?.[sortField] ?? 0);
      const safeLeft = Number.isNaN(leftNumber) ? 0 : leftNumber;
      const safeRight = Number.isNaN(rightNumber) ? 0 : rightNumber;
      return (safeLeft - safeRight) * direction;
    }

    return String(left?.[sortField] ?? '').localeCompare(String(right?.[sortField] ?? '')) * direction;
  });
};

const EadinInventory = ({ marginFromSidebar }) => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  const visibleRows = getVisibleInventoryRows(rows, { search, sortField, sortDirection });
  const filters = (
    <div className="contents">
      <div className="flex flex-row items-center gap-2">
        <input
          type="text"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search SKU or product..."
          className="w-40 sm:w-[200px] rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
        />
      </div>
    </div>
  );
  const pagination = (
    <div className="text-sm text-gray-700">
      {visibleRows.length.toLocaleString()} of {rows.length.toLocaleString()} rows
    </div>
  );

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((currentDirection) => (currentDirection === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortField(field);
    setSortDirection('asc');
  };

  useEffect(() => {
    let isMounted = true;
    fetchAllEadinInventoryRows().then((data) => {
      if (isMounted) setRows(data);
    }).catch((requestError) => {
      if (isMounted) setError(requestError?.response?.data?.error || 'Failed to load Eadin Inventory.');
    }).finally(() => {
      if (isMounted) setIsLoading(false);
    });
    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <ReportTemplate
      reportId={EADIN_INVENTORY}
      title="Eadin Inventory"
      description="Daily inventory by SKU: physical on hand, on order, kit buildable and weighted-cost inventory value."
      breadcrumbs={[{ label: 'Reports', icon: <DocumentChartBarIcon className="h-5 w-5 text-gray-400" /> }, { label: '' }]}
      marginFromSidebar={marginFromSidebar}
      onBack={() => navigate(ROUTES.REPORTS)}
      isLoading={isLoading}
      error={error}
      columns={columns}
      data={rows}
      emptyMessage="No inventory snapshot rows are available."
      filters={filters}
      exportButton={<CSVExportButton data={visibleRows} columns={columns} filename={EADIN_INVENTORY} disabled={isLoading} />}
      pagination={pagination}
      renderChildrenOnly
    >
      <div className="overflow-auto rounded border bg-white">
        {visibleRows.length === 0 ? (
          <div className="px-6 py-12 text-center text-sm text-gray-500">
            {rows.length === 0 ? 'No inventory snapshot rows are available.' : 'No inventory rows match your search.'}
          </div>
        ) : (
          <table className="min-w-max text-left text-xs">
            <thead className="sticky top-0 bg-gray-100">
              <tr>
                {columns.map((column) => {
                  const isNumeric = column.type === 'number' || column.type === 'currency';
                  const isProduct = column.key === 'product';
                  const isActive = sortField === column.key;
                  return (
                    <th
                      key={column.key}
                      scope="col"
                      aria-sort={isActive ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
                      onClick={() => handleSort(column.key)}
                      className={`whitespace-nowrap px-3 py-2 font-semibold text-gray-900 cursor-pointer select-none${isNumeric ? ' text-right' : ''}${isProduct ? ' max-w-[320px] truncate' : ''}`}
                    >
                      {column.label}
                      {isActive && (
                        <span className="ml-1 text-[10px]" aria-hidden="true">
                          {sortDirection === 'asc' ? '▲' : '▼'}
                        </span>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row, index) => (
                <tr
                  key={`${row.sku || 'sku'}-${index}`}
                  className={index % 2 ? 'bg-gray-50' : 'bg-white'}
                >
                  {columns.map((column) => {
                    const isNumeric = column.type === 'number' || column.type === 'currency';
                    const isProduct = column.key === 'product';
                    const value = row?.[column.key];
                    let displayValue = value ?? '';
                    if (column.type === 'number') {
                      displayValue = Number(value ?? 0).toLocaleString('en-US');
                    } else if (column.type === 'currency') {
                      displayValue = formatCurrencyUsdFixedTwo(value ?? 0);
                    }
                    return (
                      <td
                        key={column.key}
                        title={isProduct ? row.product : undefined}
                        className={`whitespace-nowrap px-3 py-2${isNumeric ? ' text-right' : ''}${isProduct ? ' max-w-[320px] truncate' : ''}`}
                      >
                        {displayValue}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </ReportTemplate>
  );
};

export default EadinInventory;
